import React, { useState } from 'react';
import './HelpPanel.css';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, children, defaultOpen = false }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`dp-help-panel__section ${open ? 'dp-help-panel__section--open' : ''}`}>
      <button
        className="dp-help-panel__section-header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="dp-help-panel__section-title">{title}</span>
        <span className="dp-help-panel__section-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && <div className="dp-help-panel__section-body">{children}</div>}
    </div>
  );
}

export function HelpPanel() {
  return (
    <div className="dp-help-panel">
      <h3 className="dp-help-panel__title">Help &amp; Reference</h3>

      <CollapsibleSection title="Placeholder Naming" defaultOpen>
        <p>
          Placeholders use dot-notation to represent data fields. The naming convention mirrors
          the structure of your data source.
        </p>
        <div className="dp-help-panel__examples">
          <div className="dp-help-panel__example">
            <code>client.name</code>
            <span>Client's full name</span>
          </div>
          <div className="dp-help-panel__example">
            <code>client.address.city</code>
            <span>Nested address field</span>
          </div>
          <div className="dp-help-panel__example">
            <code>bond.amount</code>
            <span>Bond amount value</span>
          </div>
          <div className="dp-help-panel__example">
            <code>policy.effective_date</code>
            <span>Policy effective date</span>
          </div>
        </div>
        <p className="dp-help-panel__tip">
          Use lowercase with dots as separators. Avoid spaces and special characters.
        </p>
      </CollapsibleSection>

      <CollapsibleSection title="Tag Prefixes">
        <p>
          Prefix a content control tag to change how it behaves during document generation.
        </p>
        <div className="dp-help-panel__prefix-list">
          <div className="dp-help-panel__prefix">
            <span className="dp-help-panel__prefix-tag dp-help-panel__prefix-tag--if">IF:</span>
            <div>
              <strong>Show if truthy</strong>
              <p>
                The content control is included in the output only when the referenced value is
                truthy (non-empty, non-null, non-false).
              </p>
              <code>IF:client.has_guarantor</code>
            </div>
          </div>
          <div className="dp-help-panel__prefix">
            <span className="dp-help-panel__prefix-tag dp-help-panel__prefix-tag--unless">UNLESS:</span>
            <div>
              <strong>Show if falsy</strong>
              <p>
                The content control is included only when the referenced value is falsy
                (empty, null, false, or missing).
              </p>
              <code>UNLESS:client.has_guarantor</code>
            </div>
          </div>
          <div className="dp-help-panel__prefix">
            <span className="dp-help-panel__prefix-tag dp-help-panel__prefix-tag--repeat">REPEAT:</span>
            <div>
              <strong>Clone for each array item</strong>
              <p>
                The content control is duplicated once for each item in the referenced array.
                Inner placeholders resolve relative to the current array item.
              </p>
              <code>REPEAT:bond.obligees</code>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Type Formatting">
        <p>
          Values are formatted based on their declared type in the placeholder catalog.
        </p>
        <table className="dp-help-panel__table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Format</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>Text</code></td>
              <td>Inserted as-is</td>
              <td>Acme Corp</td>
            </tr>
            <tr>
              <td><code>Date</code></td>
              <td>Formatted date</td>
              <td>January 15, 2026</td>
            </tr>
            <tr>
              <td><code>Number</code></td>
              <td>#,##0</td>
              <td>1,250</td>
            </tr>
            <tr>
              <td><code>Money</code></td>
              <td>$#,##0.00</td>
              <td>$50,000.00</td>
            </tr>
            <tr>
              <td><code>Boolean</code></td>
              <td>Yes / No</td>
              <td>Yes</td>
            </tr>
          </tbody>
        </table>
      </CollapsibleSection>

      <CollapsibleSection title="Workflow">
        <p>Steps to author and publish a template:</p>
        <ol className="dp-help-panel__steps">
          <li>
            <strong>Create your document</strong>
            <p>Design the layout and content in Word as usual.</p>
          </li>
          <li>
            <strong>Add placeholders</strong>
            <p>
              Insert content controls (Developer tab &gt; Rich Text or Plain Text) and set
              their tags to placeholder names from the catalog.
            </p>
          </li>
          <li>
            <strong>Validate</strong>
            <p>
              Use the Dashboard &gt; Validate Tags button to check all tags against the
              API catalog. Fix any unknown or missing required tags.
            </p>
          </li>
          <li>
            <strong>Save as version</strong>
            <p>
              Use the Save tab to upload the document as a new version of a template.
              Add change notes for traceability.
            </p>
          </li>
          <li>
            <strong>Test with preview</strong>
            <p>
              Use the Preview tab to fill placeholders with sample data and verify the
              output looks correct before publishing.
            </p>
          </li>
        </ol>
      </CollapsibleSection>

      <CollapsibleSection title="Keyboard Shortcuts">
        <p className="dp-help-panel__placeholder-text">
          Keyboard shortcuts will be added in a future release. Check back for updates.
        </p>
      </CollapsibleSection>
    </div>
  );
}
